import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie"; // Use cookies for token management
import { baseURL } from "../../App";
import "./admin.css";

const PortfolioAdmin = () => {
  const API_URL = `${baseURL}/api/portfolio`;
  const [portfolioData, setPortfolioData] = useState([]);
  const [formData, setFormData] = useState({
    image: "",
    titleEn: "",
    titleFr: "",
    descriptionEn: "",
    descriptionFr: "",
    github: "",
    demo: "",
    _id: null,
  });
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const token = Cookies.get("token");

  useEffect(() => {
    // Fetch portfolio data
    const fetchPortfolioData = async () => {
      try {
        const response = await axios.get(API_URL);
        setPortfolioData(response.data);
      } catch (err) {
        console.error("Error fetching Portfolio data:", err);
      }
    };

    fetchPortfolioData();
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "image" ? files[0] : value,
    }));
  };

  const handleSelectChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === "new") {
      setFormData({
        image: "",
        titleEn: "",
        titleFr: "",
        descriptionEn: "",
        descriptionFr: "",
        github: "",
        demo: "",
        _id: null,
      });
    } else {
      const selectedData = portfolioData.find(
        (item) => item._id === selectedId,
      );
      if (selectedData) {
        setFormData(selectedData);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    // Attached to key 'file' as per guide
    if (formData.image instanceof File) {
      data.append("file", formData.image);
    }
    data.append("titleEn", formData.titleEn);
    data.append("titleFr", formData.titleFr);
    data.append("github", formData.github);
    data.append("demo", formData.demo);
    data.append("descriptionEn", formData.descriptionEn);
    data.append("descriptionFr", formData.descriptionFr);

    try {
      if (!window.confirm("Are you sure you want to save these changes?")) {
        return;
      }

      if (formData._id) {
        // Use PATCH for updates as per guide
        const response = await axios.patch(`${API_URL}/${formData._id}`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });
        setConfirmationMessage("Portfolio updated successfully!");
        setPortfolioData((prev) =>
          prev.map((item) =>
            item._id === response.data._id ? response.data : item,
          ),
        );
      } else {
        // Guide suggests plural /api/portfolios for POST
        const response = await axios.post(`${baseURL}/api/portfolios`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });
        setConfirmationMessage("Portfolio created successfully!");
        setPortfolioData((prev) => [...prev, response.data]);
      }
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Error saving data.",
      );
      console.error("Save Error:", err.response?.data);
    }
  };

  const handleDelete = async () => {
    if (!formData._id) return;

    try {
      const response = await axios.delete(`${API_URL}/${formData._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      if (response.status === 200 || response.status === 204) {
        setConfirmationMessage("Portfolio deleted successfully!");
        setPortfolioData((prev) =>
          prev.filter((item) => item._id !== formData._id),
        );
        resetForm();
      }
    } catch (err) {
      console.error("Error deleting Portfolio data:", err);
      setConfirmationMessage(err.message || "Error deleting data.");
    }
  };

  const resetForm = () => {
    setFormData({
      image: "",
      titleEn: "",
      titleFr: "",
      descriptionEn: "",
      descriptionFr: "",
      github: "",
      demo: "",
      _id: null,
    });
  };

  return (
    <div className="admin-section">
      <h2>Portfolio Management</h2>
      <select
        className="select-item"
        onChange={handleSelectChange}
        value={formData._id || "new"}
      >
        <option value="new">New</option>
        {portfolioData.map(({ _id, titleEn }) => (
          <option className="select-item__option" key={_id} value={_id}>
            {titleEn}
          </option>
        ))}
      </select>
      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label>Project Title (EN)</label>
          <input
            type="text"
            name="titleEn"
            placeholder="Title (English)"
            value={formData.titleEn || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Project Title (FR)</label>
          <input
            type="text"
            name="titleFr"
            placeholder="Title (French)"
            value={formData.titleFr || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>GitHub URL</label>
          <input
            type="url"
            name="github"
            placeholder="GitHub URL"
            value={formData.github || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Demo URL</label>
          <input
            type="url"
            name="demo"
            placeholder="Demo URL"
            value={formData.demo || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Project Image</label>
          {formData.image && typeof formData.image === "string" && (
            <img
              src={formData.image}
              alt="Project"
              className="img-preview"
              style={{ maxWidth: "100px", display: "block", margin: "10px 0" }}
            />
          )}
          {formData.image && formData.image instanceof File && (
            <p>New image selected: {formData.image.name}</p>
          )}
          <input type="file" name="image" onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Description (EN)</label>
          <textarea
            name="descriptionEn"
            placeholder="Description (English)"
            value={formData.descriptionEn || ""}
            onChange={handleChange}
            rows="6"
            required
          />
        </div>
        <div className="form-group">
          <label>Description (FR)</label>
          <textarea
            name="descriptionFr"
            placeholder="Description (French)"
            value={formData.descriptionFr || ""}
            onChange={handleChange}
            rows="6"
            required
          />
        </div>
        <div className="btn-actions">
          <button className="btn btnPrimary" type="submit">
            Save
          </button>
          <button
            className="btn btnDanger"
            type="button"
            onClick={handleDelete}
            disabled={!formData._id} // Disable if no entry is selected
          >
            Delete
          </button>
        </div>
      </form>
      {confirmationMessage && (
        <div className="confirmation-message">{confirmationMessage}</div>
      )}
    </div>
  );
};

export default PortfolioAdmin;
